import React, { useEffect, useRef } from 'react';

import { useField } from 'formik';

import Element, { ElementProps } from '@components/Formik/Element';
import { Icon } from '@components/Icon';

import { customStyles } from './customStyles';
import Dropdown from './Dropdown';
import MenuList from './MenuList';
import OptionItem from './OptionItem';

import tailwind from '@styles/tailwind.js';

import ReactSelect, { OptionTypeBase, ValueType } from '@external/react-select';
import { useWindowSize } from '@hooks/useWindowSize';

import { Option, SingleSelectFilterType } from './types';

export interface SingleSelectFilterProps
  extends ElementProps,
    SingleSelectFilterType {}

const SingleSelectFilter: React.FC<SingleSelectFilterProps> = props => {
  const {
    name,
    options,
    inputPlaceholder,
    applyFilter,
    resetFilter,
    title,
  } = props;
  const inputId = `${name}-input`;

  const [field, , helpers] = useField<ValueType<OptionTypeBase>>(name);

  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const [selectedOption, setSelectedOption] = React.useState<
    ValueType<OptionTypeBase>
  >(field.value);

  const menuRef = useRef(null);
  const windowWidth = useWindowSize().width;
  const isAutoFocusActive =
    windowWidth > parseInt(tailwind.theme.screens.desktop, 10);

  useEffect(() => {
    if (field.value?.length === 0) {
      setSelectedOption([]);
    }
  }, [field.value, setSelectedOption]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleChange = (option: ValueType<OptionTypeBase>) => {
    setSelectedOption([option]);
    helpers.setValue([option]);
    applyFilter([option] as Option[]);
    toggleOpen();
  };

  const handleClearAll = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    resetFilter(name);
    setSelectedOption([]);
    helpers.setValue([]);
    if (isOpen) {
      toggleOpen();
    }
  };

  const target = (
    <div
      className="flex items-baseline cursor-pointer tap-highlight-color-transparent"
      data-testid="dropdown"
      onClick={toggleOpen}
    >
      <span className="text-xs">{title}</span>
      {!!selectedOption?.length && (
        <span className="text-dark-blue-400 font-bold self-center ml-1 text-3xs">
          {selectedOption?.length}
        </span>
      )}
      {!!selectedOption?.length && (
        <div
          className="self-center z-20"
          data-testid="clear-all-selected-button"
          onClick={handleClearAll}
        >
          <Icon
            color="gray-300"
            name="circles/close"
            size="14"
            className="ml-1 text-gray-300 close-svg"
          />
        </div>
      )}
      <Icon
        name={`arrows/${isOpen ? 'up' : 'down'}`}
        size="14"
        color="dark-blue-400"
        className="self-center ml-auto desktop:ml-2 mt-px"
      />
    </div>
  );

  return (
    <Element {...props}>
      <Dropdown isOpen={isOpen} onClose={toggleOpen} target={target}>
        <ReactSelect
          backspaceRemovesValue={false}
          controlShouldRenderValue={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          isClearable={false}
          menuIsOpen
          autoFocus={isAutoFocusActive}
          styles={customStyles}
          onChange={handleChange}
          options={options}
          tabSelectsValue={false}
          inputId={inputId}
          windowWidth={windowWidth}
          value={selectedOption}
          isSearchable={false}
          placeholder={inputPlaceholder}
          menuRef={menuRef}
          components={{
            Option: OptionItem,
            MenuList,
            IndicatorSeparator: null,
            DropdownIndicator: null,
          }}
        />
      </Dropdown>
    </Element>
  );
};

export default SingleSelectFilter;
